import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSectionQuestions } from "../../..";
import { Answer } from "../../../types/Rows";
import { QuestionWithAnswer } from "./QuestionWithAnswer";
import { updateReportAnswers } from "../../../api/reportsApi";
import { successToast, failureSaveToast } from "../../../helpers/successToast";
import { v4 as uuidv4 } from "uuid";

export const Row = ({
  input,
  reportId,
  index,
  depth = 0,
}: {
  input: Answer;
  index: string;
  reportId: string;
  depth: number;
}) => {
  const [comment, setComment] = useState<string>(input.comment);
  const [answer, setAnswer] = useState<string>(input.answer);
  const updateQuestionAnswer = useSectionQuestions(
    (state) => state.updateQuestionAnswer
  );
  const queryClient = useQueryClient();

  const [children, setChildren] = useState<Answer[]>([]);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isFollowUp, setFollowUp] = useState<boolean>(
    input.isPresentInFollowUpLetter
  );

  // Mutation for immediate saving with optimistic updates
  const { mutateAsync: saveAnswerImmediately } = useMutation(
    async (updatedAnswer: Answer) => {
      const correlationId = uuidv4();
      return updateReportAnswers(reportId, [updatedAnswer], correlationId);
    },
    {
      onMutate: async (updatedAnswer) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries([reportId, "Sections"]);

        // Snapshot the previous value
        const previousSections = queryClient.getQueryData([reportId, "Sections"]);

        // Optimistically update to the new value
        queryClient.setQueryData([reportId, "Sections"], (old: any) => {
          if (!old) return old;
          
          const updateQuestionRecursively = (questions: Answer[]): Answer[] => {
            return questions.map((question: Answer) => {
              if (question.answerId === updatedAnswer.answerId) {
                return { ...question, ...updatedAnswer };
              }
              
              // Update children recursively if they exist
              if (question.children && question.children.length > 0) {
                return {
                  ...question,
                  children: updateQuestionRecursively(question.children)
                };
              }
              
              return question;
            });
          };
          
          return old.map((section: any) => ({
            ...section,
            questionsWithAnswer: updateQuestionRecursively(section.questionsWithAnswer)
          }));
        });

        // Return a context object with the snapshotted value
        return { previousSections };
      },
      onSuccess: () => {
        // Show success toast when save is successful
        successToast();
      },
      onError: (err, updatedAnswer, context) => {
        // If the mutation fails, use the context returned from onMutate to roll back
        queryClient.setQueryData([reportId, "Sections"], context?.previousSections);
        // Show error toast when save fails
        failureSaveToast("odpowiedzi");
      },
      onSettled: () => {
        // Always refetch after error or success to ensure we have the latest data
        // But this won't cause a full re-render since the data should be the same
        queryClient.invalidateQueries([reportId, "Sections"]);
      },
    }
  );

  useEffect(() => {
    if (input.children) {
      setChildren(input.children);
    }
  }, [input.children]);

  // Handle immediate saving when answer changes (radio buttons)
  const handleAnswerChange = async (newAnswer: string) => {
    setAnswer(newAnswer);
    const updatedAnswer: Answer = {
      ...input,
      answerId: input.answerId,
      comment: comment,
      answer: newAnswer,
      isPresentInFollowUpLetter: isFollowUp,
    };
    
    // Update local state immediately
    updateQuestionAnswer(updatedAnswer);
    
    // Save to server immediately
    try {
      await saveAnswerImmediately(updatedAnswer);
      // Success toast is handled in mutation's onSuccess
    } catch (error) {
      console.error("Failed to save answer:", error);
      // Error toast is handled in mutation's onError
    }
  };

  // Handle immediate saving when follow-up status changes  
  const handleFollowUpChange = async (newFollowUp: boolean) => {
    setFollowUp(newFollowUp);
    const updatedAnswer: Answer = {
      ...input,
      answerId: input.answerId,
      comment: comment,
      answer: answer,
      isPresentInFollowUpLetter: newFollowUp,
    };
    
    // Update local state immediately
    updateQuestionAnswer(updatedAnswer);
    
    // Save to server immediately
    try {
      await saveAnswerImmediately(updatedAnswer);
      // Success toast is handled in mutation's onSuccess
    } catch (error) {
      console.error("Failed to save follow-up status:", error);
      // Error toast is handled in mutation's onError
    }
  };

  // Handle comment save (called from modal)
  const handleCommentSave = async (newComment: string) => {
    setComment(newComment);
    const updatedAnswer: Answer = {
      ...input,
      answerId: input.answerId,
      comment: newComment,
      answer: answer,
      isPresentInFollowUpLetter: isFollowUp,
    };
    
    // Update local state immediately
    updateQuestionAnswer(updatedAnswer);
    
    // Save to server immediately
    try {
      await saveAnswerImmediately(updatedAnswer);
      // Success toast is handled in mutation's onSuccess
    } catch (error) {
      console.error("Failed to save comment:", error);
      // Error toast is handled in mutation's onError
      throw error; // Re-throw for modal error handling
    }
  };

  // Note: We removed the useEffect that was updating local state on changes
  // since we now handle all updates immediately through the specific handlers:
  // - handleAnswerChange for radio buttons
  // - handleFollowUpChange for follow-up checkbox  
  // - handleCommentSave for comments (via modal)

  useEffect(() => {
    if (depth > 0) {
      setIsVisible(true);
      setTimeout(() => setIsVisible(false), 2000); // Highlight for 2 seconds
    }
  }, [index, depth]);

  return (
    <div
      id={`row-${index}`}
      className={`border-l-4 ${depth > 0 ? "border-blue-500 pl-4" : ""} ${
        depth > 0 && isVisible
          ? "bg-yellow-100 transition-colors duration-1000 ease-in-out"
          : "bg-[var(--sc-surface)] transition-colors duration-1000 ease-in-out"
      }`}
    >
      <QuestionWithAnswer
        index={index}
        input={input}
        comment={comment}
        setComment={handleCommentSave}
        answer={answer}
        isFollowUp={isFollowUp}
        setFollowUp={handleFollowUpChange}
        setAnswer={handleAnswerChange}
        depth={0}
        reportId={reportId}
        saveAnswerImmediately={saveAnswerImmediately}
      />
      {children
        ?.filter((x) => x.requiredAnswerToBeVisible == answer.toLowerCase())
        ?.sort((a, b) => (new Date(a.created) > new Date(b.created) ? 1 : -1))
        .map((item, i) => (
          <Row
            index={index + "." + (i + 1)}
            key={item.answerId}
            input={item}
            reportId={reportId}
            depth={1}
          ></Row>
        ))}
    </div>
  );
};
